import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Popover,
  Grid,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useInputLocking } from '../../../../../../hooks/useInputLocking';
import { InputLockReason } from '../../../../../../services/InputController';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  presetColors?: string[];
  label?: string;
}

const ColorSwatch = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})<{ color: string; isSelected: boolean }>(({ theme, color, isSelected }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: color,
  border: `2px solid ${isSelected ? theme.palette.primary.main : 'transparent'}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: theme.shadows[4],
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CustomColorInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const defaultPresetColors = [
  '#3f51b5', // Indigo
  '#2196f3', // Blue
  '#03a9f4', // Light Blue
  '#00bcd4', // Cyan
  '#009688', // Teal
  '#4caf50', // Green
  '#8bc34a', // Light Green
  '#cddc39', // Lime
  '#ffeb3b', // Yellow
  '#ff9800', // Orange
  '#ff5722', // Deep Orange
  '#f44336', // Red
  '#e91e63', // Pink
  '#9c27b0', // Purple
  '#673ab7', // Deep Purple
  '#607d8b', // Blue Grey
  '#795548', // Brown
  '#9e9e9e', // Grey
  '#000000', // Black
  '#ffffff', // White
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  presetColors = defaultPresetColors,
  label = 'Color'
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [customColor, setCustomColor] = useState(value);

  // Input locking for custom color input
  const {
    inputFocused,
    handleInputFocus,
    handleInputBlur,
    handleKeyDown,
    handleKeyUp,
    KeyboardCaptureLayer
  } = useInputLocking({
    lockReason: InputLockReason.MAP_EDITOR,
    debugLabel: 'ColorPicker',
    enableDebugLogs: false
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (color: string) => {
    onChange(color);
    setCustomColor(color);
    handleClose();
  };

  const handleCustomColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = event.target.value;
    setCustomColor(newColor);
    
    // Validate hex color format
    if (/^#[0-9A-F]{6}$/i.test(newColor)) {
      onChange(newColor);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'color-picker-popover' : undefined;

  return (
    <>
      {/* Keyboard capture layer when input is focused */}
      {inputFocused && <KeyboardCaptureLayer />}
      
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Click to open color picker">
          <IconButton
            onClick={handleClick}
            sx={{
              p: 0,
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            <ColorSwatch
              color={value}
              isSelected={false}
              sx={{
                width: 40,
                height: 40,
                border: '2px solid',
                borderColor: 'divider',
              }}
            />
          </IconButton>
        </Tooltip>

        <CustomColorInput
          size="small"
          value={customColor}
          onChange={handleCustomColorChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          placeholder="#000000"
          InputProps={{
            startAdornment: <InputAdornment position="start">#</InputAdornment>,
          }}
          sx={{ width: 120 }}
        />
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 280,
            maxWidth: 320,
          },
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Preset Colors
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {presetColors.map((color) => (
            <Grid item key={color}>
              <Tooltip title={color}>
                <ColorSwatch
                  color={color}
                  isSelected={value === color}
                  onClick={() => handleColorSelect(color)}
                />
              </Tooltip>
            </Grid>
          ))}
        </Grid>

        <Typography variant="subtitle2" gutterBottom>
          Custom Color
        </Typography>
        
        <CustomColorInput
          fullWidth
          size="small"
          value={customColor}
          onChange={handleCustomColorChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          placeholder="#000000"
          InputProps={{
            startAdornment: <InputAdornment position="start">#</InputAdornment>,
          }}
          helperText="Enter a 6-digit hex color code"
        />
      </Popover>
    </Box>
    </>
  );
}; 